import React, { useState } from "react";
import { Collapse, Col, Row } from "react-bootstrap";
import { CaretRightFill, CaretDownFill } from "react-bootstrap-icons";

import { CustomerDetailsProps, CustomerInfoProps } from "../../pages/SalesDocuments-v2";
import { formatCustomerDetails } from "../../services/salesDocuments-v2.service";

const ReadOnlyField = ({ label, value }: { label: string; value: string | string[] }) => (
  <div className="sales-doc-field read-only-field">
    <label>{label}</label>
    {Array.isArray(value) ? (
      value.map((line, i) => (
        <input key={i} type="text" className="form-control mb-1" value={line} readOnly />
      ))
    ) : (
      <input type="text" className="form-control" value={value} readOnly />
    )}
  </div>
);

interface SalesDocHeaderProps {
  customerDetails: CustomerDetailsProps | null;
  customerInfo: CustomerInfoProps | null;
  companyName: string;
  customerId: string;
  customerApiDataLoaded: boolean;
  customerInfoValid: boolean;
  setCustomerDetails: (value: CustomerDetailsProps | null) => void;
  setCustomerInfo: (value: CustomerInfoProps | null) => void;
  setCompanyName: (value: string) => void;
  setCustomerId: (value: string) => void;
  setCustomerApiDataLoaded: (value: boolean) => void;
}

const SalesDocHeader: React.FC<SalesDocHeaderProps> = ({
  customerDetails,
  setCustomerDetails,
  customerInfo,
  setCustomerInfo,
  companyName,
  setCompanyName,
  customerId,
  setCustomerId,
  customerApiDataLoaded,
  setCustomerApiDataLoaded,
  customerInfoValid,
}) => {
  const isSoleTrader = customerDetails?.typeOfBusiness === "Sole Trader";
  const [loading, setLoading] = useState<boolean>(false);
  const [customerIsValid, setCustomerIsValid] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const toggleOpen = () => {
    setIsOpen(!isOpen);
  };

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!customerInfo) return; // Guard against null

    // Pass the whole object directly
    setCustomerInfo({
      ...customerInfo,
      dateOfBirth: e.target.value,
    });
  };

  const handleRetrieveCustomer = async () => {
    setCustomerDetails(null);
    setCustomerInfo(null);
    setCompanyName("");
    setCustomerApiDataLoaded(false);
    setCustomerIsValid(true);
    setLoading(true);
    const result = await formatCustomerDetails(customerId);
    setLoading(false);

    if (!result.success) {
      setCustomerIsValid(false);
      // TODO: More robust error handling
      // if (result.statusCode === 404) {
      //   setError("Customer not found");
      // } else {
      //   setError(`Failed to load customer: ${result.error}`);
      // }
      return;
    }

    // result.data is now FormattedCustomer, fully narrowed
    setCustomerIsValid(true);
    setCustomerDetails(result.data.customerDetails);
    setCustomerInfo(result.data.customerInfo);
    setCompanyName(result.data.companyName);
    setCustomerApiDataLoaded(true);
  };

  return (
    <div className="sales-doc-section mx-auto sales-doc-header">
      <div className="sales-doc-field">
        <label htmlFor="customer-id">Customer ID</label>
        <input
          id="customer-id"
          name="customer_id"
          type="text"
          minLength={18}
          maxLength={18}
          required
          value={customerId}
          onChange={(e) => {
            setCustomerId(e.target.value);
          }}
        />
        <button
          type="button"
          className="sales-doc-btn-secondary mt-3"
          disabled={loading}
          onClick={() => {
            void handleRetrieveCustomer();
          }}
        >
          Retrieve Details
        </button>

        {!customerIsValid && (
          <div className="alert alert-danger">⚠️ Given customer ID was not found in Zoho CRM.</div>
        )}

        {loading && (
          <div className="d-flex justify-content-center mt-2">
            <div className="spinner-border spinner-border-sm text-secondary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>

      {customerApiDataLoaded && customerDetails && customerIsValid && (
        <>
          <ReadOnlyField label="Company Name" value={companyName} />

          <div
            className={`dob-field sales-doc-field read-only-field ${isSoleTrader ? "visible" : ""}`}
          >
            <label htmlFor="dob">Date of Birth</label>
            <input
              id="dob"
              name="date_of_birth"
              type="date"
              className="form-control"
              onChange={handleDobChange}
              required
            />
          </div>
          <div className="card border-0">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                toggleOpen();
              }}
            >
              Extra Details {isOpen ? <CaretDownFill /> : <CaretRightFill />}{" "}
            </button>
          </div>
          <Collapse in={isOpen}>
            <div className="container mt-5">
              <Row>
                <Col>
                  <ReadOnlyField label="Business Type" value={customerDetails.typeOfBusiness} />
                  <ReadOnlyField
                    label="Registration Number"
                    value={customerDetails.registrationNumber ?? ""}
                  />
                  <span style={{ display: "none" }}>
                    <ReadOnlyField
                      label="Business Address"
                      value={customerDetails.businessAddress}
                    />
                  </span>
                </Col>
                <Col>
                  <ReadOnlyField label="Contact Name" value={customerInfo?.contactName ?? ""} />
                  <ReadOnlyField label="Phone" value={customerInfo?.phoneNumber ?? ""} />
                  <ReadOnlyField label="Email" value={customerInfo?.emailAddress ?? ""} />
                </Col>
              </Row>
            </div>
          </Collapse>
        </>
      )}

      {!customerInfoValid && (
        <div className="alert alert-info mt-3 mb-3">
          Date of Birth is required for Sole Traders.
        </div>
      )}
    </div>
  );
};

export default SalesDocHeader;
